// Client PDF Generator
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Colors matching website palette
const colors = {
  burgundy: '#9b2242',
  sage: '#687864',
  blush: '#ef7596',
  mauve: '#755B69',
  lightBlush: '#ffe8f0',
  lightSage: '#e8f0e0'
};

function generateBeautifulClientPDF(result) {
  try {
    console.log("Starting PDF generation with data structure:", result);
    // Check if jsPDF is properly loaded
    if (typeof jsPDF !== 'function') {
      console.error("jsPDF is not properly loaded. Type:", typeof jsPDF);
      alert("Error: PDF library not loaded correctly");
      return;
    }
    
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Add custom font if needed
    // doc.addFont('fonts/Marcellus-Regular.ttf', 'Marcellus', 'normal');
    // doc.setFont('Marcellus');

    // Page dimensions
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    
    // Add decorative header
    doc.setFillColor(colors.blush);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Add title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('KISMET LAB - Tu diagnóstico personalizado', pageWidth / 2, 20, { align: 'center' });
    
    // Add user info section
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    
    // User info
    const userFirstName = result.user_first_name || 'Cliente';
    const userLastName = result.user_last_name || '';
    const userName = `${userFirstName} ${userLastName}`.trim();
    const formattedDate = result.inference_datetime || new Date().toLocaleDateString();
    
    doc.text(`Diagnóstico para: ${userName}`, margin, 50);
    doc.text(`Fecha: ${formattedDate}`, margin, 60);
    
    // Main diagnosis section
    doc.setFillColor(colors.lightBlush);
    doc.roundedRect(margin, 70, contentWidth, 30, 3, 3, 'F');
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.burgundy);
    doc.text('Diagnóstico principal', margin + 5, 80);
    
    doc.setFontSize(14);
    doc.setTextColor(colors.mauve);
    doc.setFont('helvetica', 'normal');
    doc.text(result.active_diagnosis || 'Personalizado', margin + 5, 90);
    
    // Top conditions section
    let yPosition = 110;
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.burgundy);
    doc.text('Principales condiciones de tu cabello:', margin, yPosition);
    
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colors.mauve);
    
    // Convert top diagnoses object to array of {name, score} pairs
    const topDiagnoses = Object.entries(result.top_3_active_diagnosis || {})
      .map(([name, score]) => ({ name, score }))
      .sort((a, b) => b.score - a.score);
      
    topDiagnoses.forEach((diagnosis, index) => {
      doc.text(`${index + 1}. ${diagnosis.name}`, margin + 5, yPosition);
      yPosition += 7;
    });
    
    // Active ingredients section
    yPosition += 15;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.burgundy);
    doc.text('Ingredientes activos recomendados:', margin, yPosition);
    
    yPosition += 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colors.mauve);
    
    // Add ingredients as a formatted list
    const activeIngredients = result.active_ingredients || [];
    activeIngredients.forEach((ingredient, index) => {
      doc.text(`• ${ingredient}`, margin + 5, yPosition);
      yPosition += 7;
    });
    
    // Recommended vitamins section
    if (result.recommended_vitamins) {
      let vitamins = result.recommended_vitamins;
      
      // Handle case where recommended_vitamins is a JSON string
      if (typeof vitamins === 'string') {
        try {
          vitamins = JSON.parse(vitamins);
        } catch (e) {
          console.error("Error parsing recommended_vitamins:", e);
          vitamins = [];
        }
      }
      
      if (vitamins && vitamins.length > 0) {
        yPosition += 15;
        
        doc.setFillColor(colors.lightSage);
        doc.roundedRect(margin, yPosition, contentWidth, 10 + (vitamins.length * 25), 3, 3, 'F');
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.sage);
        doc.text('Vitaminas recomendadas:', margin + 5, yPosition + 8);
        
        yPosition += 15;
        
        // Table headers
        doc.setFontSize(12);
        doc.setTextColor(colors.sage);
        
        // Create vitamin table
        const vitaminsData = vitamins.map(vitamin => [
          vitamin.name,
          vitamin.function
        ]);
        
        doc.autoTable({
          startY: yPosition,
          head: [['Vitamina', 'Beneficio']],
          body: vitaminsData,
          theme: 'grid',
          styles: {
            fontSize: 10,
            textColor: [80, 80, 80],
            lineColor: [200, 200, 200],
            lineWidth: 0.1
          },
          headStyles: {
            fillColor: [colors.sage.replace('#', '')],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { left: margin, right: margin }
        });
        
        yPosition = doc.autoTable.previous.finalY + 10;
      }
    }
    
    // Add summary section if available
    if (result.llm_summary) {
      // If we're running out of space on the first page, add a new page
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(colors.burgundy);
      doc.text('Resumen personalizado:', margin, yPosition);
      
      yPosition += 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      // Split the summary into lines that fit the page width
      const splitText = doc.splitTextToSize(result.llm_summary, contentWidth);
      doc.text(splitText, margin, yPosition);
    }
    
    // Add footer
    const footerYPos = pageHeight - 15;
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text('© Kismet Lab - Fórmulas personalizadas para tu cabello', pageWidth / 2, footerYPos, { align: 'center' });
    doc.text('www.kismetlab.ai', pageWidth / 2, footerYPos + 5, { align: 'center' });

    // Save the PDF
    console.log("About to save PDF...");
    doc.save('Kismet_Diagnóstico_Personalizado.pdf');
    console.log("PDF generated successfully");
  } catch (error) {
    console.error("Error generating PDF:", error);
    alert("Hubo un problema al generar el PDF. Por favor, inténtelo de nuevo. Error: " + error.message);
  }
}

export { generateBeautifulClientPDF }; 